(() => {
  'use strict';
  const canonicalShipping = (() => {
const SERVICE_CODE = "MINI_POWER_SMART_SHIPPING_V1";

const OTHER_ALLOWED_COUNTRIES = new Set([
  "BE",
  "BG",
  "CH",
  "HR",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE"
]);

const SHIPPING_CLASSES = new Set([
  "standard",
  "small",
  "standalone_panel",
  "panel_kit",
  "freight"
]);

const LEAD_CLASSES = new Set(["local", "remote", "preorder"]);

function emptyResult(reason) {
  return { rates: [], reason };
}

function normalizeCountry(destination) {
  const value =
    destination?.country_code ??
    destination?.countryCode ??
    destination?.country;
  return typeof value === "string" ? value.trim().toUpperCase() : "";
}

function normalizeDate(value) {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ||
    parsed.toISOString().slice(0, 10) !== value
    ? null
    : value;
}

function todayUtc(now) {
  const date = now instanceof Date ? now : new Date(now);
  return Number.isNaN(date.getTime())
    ? new Date().toISOString().slice(0, 10)
    : date.toISOString().slice(0, 10);
}

function lookupClassification(classificationMap, variantId) {
  const key = String(variantId);

  if (typeof classificationMap === "function") {
    return classificationMap(key);
  }
  if (classificationMap instanceof Map) {
    return classificationMap.get(key) ?? classificationMap.get(variantId);
  }
  if (classificationMap && typeof classificationMap === "object") {
    return classificationMap[key];
  }
  return undefined;
}

function lookupAliasSourceId(taxPeerAliasMap, peerVariantId) {
  const key = String(peerVariantId);
  if (taxPeerAliasMap instanceof Map) {
    return taxPeerAliasMap.get(key) ?? taxPeerAliasMap.get(peerVariantId);
  }
  if (taxPeerAliasMap && typeof taxPeerAliasMap === "object") {
    return taxPeerAliasMap[key];
  }
  return undefined;
}

function lookupItemClassification(classificationMap, taxPeerAliasMap, item) {
  const direct = lookupClassification(classificationMap, item.variant_id);
  if (direct !== null && typeof direct === "object") {
    return direct;
  }

  const aliasSourceId = lookupAliasSourceId(
    taxPeerAliasMap,
    item.variant_id
  );
  if (
    aliasSourceId !== undefined &&
    String(aliasSourceId) !== String(item.variant_id)
  ) {
    const aliasedSource = lookupClassification(classificationMap, aliasSourceId);
    if (
      aliasedSource !== null &&
      typeof aliasedSource === "object" &&
      aliasedSource.status === "ACTIVE"
    ) {
      return aliasedSource;
    }
  }

  if (typeof item.sku !== "string") {
    return direct;
  }
  const taxPeerMatch = item.sku.trim().match(/^MP-TAX-(\d+)$/);
  if (!taxPeerMatch || taxPeerMatch[1] === String(item.variant_id)) {
    return direct;
  }

  const source = lookupClassification(classificationMap, taxPeerMatch[1]);
  return source !== null &&
    typeof source === "object" &&
    source.status === "ACTIVE"
    ? source
    : direct;
}

function normalizeClassification(raw) {
  const isKnown = raw !== null && typeof raw === "object";
  const rawShippingClass = raw?.shipping_class === "small_item"
    ? "small"
    : raw?.shipping_class;
  const shippingClass = SHIPPING_CLASSES.has(rawShippingClass)
    ? rawShippingClass
    : "standard";
  const leadClass = LEAD_CLASSES.has(raw?.lead_class)
    ? raw.lead_class
    : "remote";
  const panelQuantity = Number.isInteger(raw?.panel_qty_in_bundle) &&
    raw.panel_qty_in_bundle > 0
    ? raw.panel_qty_in_bundle
    : shippingClass === "standalone_panel"
      ? 1
      : 0;
  const hasAuthorizedActualWeight =
    typeof raw?.weight_source === "string" &&
    (
      /^billbee(?:\b|_)/i.test(raw.weight_source.trim()) ||
      raw.weight_source.trim() === "shopify_temporary_fallback"
    ) &&
    typeof raw?.actual_unit_weight_grams === "number" &&
    Number.isFinite(raw.actual_unit_weight_grams) &&
    raw.actual_unit_weight_grams >= 0;
  const allowedCountryCodes = Array.isArray(raw?.allowed_country_codes)
    ? [...new Set(raw.allowed_country_codes
      .filter((code) => typeof code === "string")
      .map((code) => code.trim().toUpperCase())
      .filter((code) => /^[A-Z]{2}$/.test(code)))]
    : null;
  const localMin = raw?.de_delivery_days_min;
  const localMax = raw?.de_delivery_days_max;
  const hasLocalDays = Number.isInteger(localMin) && Number.isInteger(localMax) &&
    localMin >= 1 && localMax >= localMin && localMax <= 60;

  return {
    isKnown,
    shippingClass,
    leadClass,
    availableFrom: normalizeDate(raw?.available_from),
    localDeliveryDays: hasLocalDays ? { min: localMin, max: localMax } : null,
    panelQuantity,
    // Shopify's per-request callback weight is deliberately not used for the
    // >40 kg rule. Actual weight comes from the persisted classification:
    // Billbee first, or the explicitly authorized temporary Shopify snapshot.
    actualUnitWeightGrams: hasAuthorizedActualWeight
      ? raw.actual_unit_weight_grams
      : null,
    weightSource: hasAuthorizedActualWeight
      ? raw.weight_source.trim()
      : null,
    allowedCountryCodes,
    isBattery: raw?.is_battery === true,
    containsSolarPanel:
      raw?.contains_solar_panel === true ||
      shippingClass === "standalone_panel" ||
      shippingClass === "panel_kit",
    // This privilege is intentionally opt-in. Missing, truthy strings, and 1
    // must all remain false until supported by verified product evidence.
    absorbsExtraPanelShipping: raw?.absorbs_extra_panel_shipping === true
  };
}

function validatePayload(payload) {
  const rate = payload?.rate;
  if (!rate || typeof rate !== "object") {
    return false;
  }
  if (!rate.destination || typeof rate.destination !== "object") {
    return false;
  }
  if (!Array.isArray(rate.items) || rate.items.length === 0) {
    return false;
  }
  if (typeof rate.currency !== "string") {
    return false;
  }

  return rate.items.every((item) => {
    const quantity = Number(item?.quantity);
    const grams = item?.grams ?? 0;
    return item &&
      item.variant_id !== null &&
      item.variant_id !== undefined &&
      Number.isInteger(quantity) &&
      quantity > 0 &&
      typeof grams === "number" &&
      Number.isFinite(grams) &&
      grams >= 0;
  });
}

function basePriceForCountry(country) {
  if (country === "DE") return 0;
  if (country === "AT") return 1490;
  if (OTHER_ALLOWED_COUNTRIES.has(country)) return 1990;
  return null;
}

function freightPriceForCountry(country) {
  if (country === "DE") return 0;
  if (country === "AT") return 4900;
  return 6900;
}

function formatGermanDate(isoDate) {
  const [year, month, day] = isoDate.split("-");
  return `${day}.${month}.${year}`;
}

function addBusinessDaysUtc(startDate, businessDays) {
  const result = new Date(`${startDate}T00:00:00Z`);
  let remaining = businessDays;

  while (remaining > 0) {
    result.setUTCDate(result.getUTCDate() + 1);
    const weekday = result.getUTCDay();
    if (weekday !== 0 && weekday !== 6) {
      remaining -= 1;
    }
  }

  return result.toISOString().slice(0, 10);
}

function requiresSpeditionForItem(item) {
  return item.classification.containsSolarPanel ||
    item.classification.actualUnitWeightGrams > 40_000;
}

function determineSla(items, now, country) {
  let maxNonPreorderWorkdays = 0;
  let latestPreorderDate = null;
  let hasInternationalRemote = false;
  let hasInternationalStandard = false;
  let hasInternationalSpedition = false;
  let hasLocalOverride = false;
  let maxMinimumWorkdays = 0;

  for (const item of items) {
    const requiresSpedition = requiresSpeditionForItem(item);
    const localDays = item.classification.localDeliveryDays;
    if (country === "DE" && item.classification.leadClass === "local" && localDays) {
      hasLocalOverride = true;
      maxMinimumWorkdays = Math.max(maxMinimumWorkdays, localDays.min);
      maxNonPreorderWorkdays = Math.max(maxNonPreorderWorkdays, localDays.max);
      continue;
    }
    if (item.classification.leadClass !== "preorder") {
      maxMinimumWorkdays = Math.max(maxMinimumWorkdays, item.classification.leadClass === "remote" ? 4 : 2);
    }

    if (
      item.classification.leadClass === "remote" &&
      country !== "DE"
    ) {
      maxNonPreorderWorkdays = Math.max(maxNonPreorderWorkdays, 8);
      hasInternationalRemote = true;
      if (requiresSpedition) hasInternationalSpedition = true;
    } else if (
      item.classification.leadClass === "local" &&
      country !== "DE" &&
      !requiresSpedition
    ) {
      maxNonPreorderWorkdays = Math.max(maxNonPreorderWorkdays, 8);
      hasInternationalStandard = true;
    } else if (
      item.classification.leadClass !== "preorder" &&
      requiresSpedition &&
      country !== "DE"
    ) {
      maxNonPreorderWorkdays = Math.max(
        maxNonPreorderWorkdays,
        8,
      );
      hasInternationalSpedition = true;
    } else if (item.classification.leadClass === "remote") {
      maxNonPreorderWorkdays = Math.max(
        maxNonPreorderWorkdays,
        7,
      );
    } else if (
      item.classification.leadClass !== "preorder" &&
      requiresSpedition
    ) {
      maxNonPreorderWorkdays = Math.max(maxNonPreorderWorkdays, 4);
    } else if (item.classification.leadClass === "local") {
      maxNonPreorderWorkdays = Math.max(maxNonPreorderWorkdays, 3);
    }

    if (item.classification.leadClass === "preorder") {
      const availableFrom = item.classification.availableFrom;
      if (availableFrom && availableFrom >= todayUtc(now)) {
        latestPreorderDate =
          latestPreorderDate === null || availableFrom > latestPreorderDate
            ? availableFrom
            : latestPreorderDate;
      } else {
        // A missing or stale preorder date must never produce an obsolete
        // promise. Fall back to the conservative remote-warehouse SLA.
        maxNonPreorderWorkdays = Math.max(
          maxNonPreorderWorkdays,
          country !== "DE" && requiresSpedition ? 8 : 7,
        );
        maxMinimumWorkdays = Math.max(maxMinimumWorkdays, country !== "DE" && requiresSpedition ? 2 : 4);
      }
    }
  }

  if (latestPreorderDate) {
    const nonPreorderDeadline = maxNonPreorderWorkdays > 0
      ? addBusinessDaysUtc(todayUtc(now), maxNonPreorderWorkdays)
      : null;
    if (
      nonPreorderDeadline === null ||
      latestPreorderDate > nonPreorderDeadline
    ) {
      const formatted = formatGermanDate(latestPreorderDate);
      return {
        key: `preorder:${latestPreorderDate}`,
        serviceLabel: `Versand ab ${formatted}`,
        description: `Versand nach Verfügbarkeit ab ${formatted}`
      };
    }
  }

  if (hasLocalOverride) {
    return {
      key: `local_override:${maxMinimumWorkdays}-${maxNonPreorderWorkdays}`,
      serviceLabel: `${maxMinimumWorkdays}-${maxNonPreorderWorkdays} Werktage`,
      description: `Voraussichtliche Lieferzeit: ${maxMinimumWorkdays}–${maxNonPreorderWorkdays} Werktage`
    };
  }

  if (maxNonPreorderWorkdays >= 7) {
    if (maxNonPreorderWorkdays >= 8) {
      if (hasInternationalRemote) {
        return {
          key: "remote_international",
          serviceLabel: "4-8 Werktage",
          description: "Voraussichtliche Lieferzeit: 4–8 Werktage"
        };
      }
      if (hasInternationalStandard && !hasInternationalSpedition) {
        return {
          key: "standard_international",
          serviceLabel: "2-8 Werktage",
          description: "Voraussichtliche Lieferzeit: 2-8 Werktage"
        };
      }
      return {
        key: "spedition_international",
        serviceLabel: "2-8 Werktage",
        description: "Voraussichtliche Lieferzeit: 2–8 Werktage"
      };
    }
    return {
      key: "remote",
      serviceLabel: "4-7 Werktage",
      description: "Voraussichtliche Lieferzeit: 4–7 Werktage"
    };
  }

  if (maxNonPreorderWorkdays >= 4) {
    return {
      key: "panel",
      serviceLabel: "2-4 Werktage",
      description: "Voraussichtliche Lieferzeit: 2–4 Werktage"
    };
  }

  return {
    key: "local",
    serviceLabel: "2-3 Werktage",
    description: "Voraussichtliche Lieferzeit: 2–3 Werktage"
  };
}

function calculateDecision(
  payload,
  {
    classificationMap = {},
    taxPeerAliasMap = {},
    now = new Date()
  } = {}
) {
  if (!validatePayload(payload)) {
    return emptyResult("invalid_payload");
  }

  const currency = payload.rate.currency.trim().toUpperCase();
  if (currency !== "EUR") {
    return emptyResult("unsupported_currency");
  }

  const country = normalizeCountry(payload.rate.destination);
  const basePrice = basePriceForCountry(country);
  if (basePrice === null) {
    return emptyResult("unsupported_country");
  }

  const items = payload.rate.items.map((item) => ({
    quantity: Number(item.quantity),
    // This is the existing Shopify sentinel weight. It is retained only for
    // the explicitly approved 0–79 g small-item tier and is never used for
    // physical freight classification.
    legacyEncodedGrams: item.grams ?? 0,
    classification: normalizeClassification(
      lookupItemClassification(classificationMap, taxPeerAliasMap, item)
    )
  }));
  if (items.some((item) => !item.classification.isKnown)) {
    return emptyResult("unknown_variant");
  }
  const hasCountryRestrictionConflict = items.some(
    (item) =>
      item.classification.allowedCountryCodes !== null &&
      !item.classification.allowedCountryCodes.includes(country)
  );
  if (hasCountryRestrictionConflict) {
    return emptyResult("country_not_allowed_for_item");
  }

  const hasFreight = items.some(
    (item) => item.classification.actualUnitWeightGrams > 40_000
  );
  const hasSolarPanel = items.some(
    (item) => item.classification.containsSolarPanel
  );
  const requiresSpedition = items.some(requiresSpeditionForItem);
  const standalonePanelQuantity = items.reduce(
    (total, item) =>
      item.classification.shippingClass === "standalone_panel"
        ? total + item.quantity * item.classification.panelQuantity
        : total,
    0
  );
  const hasQualifyingPanelKit = items.some(
    (item) =>
      item.classification.shippingClass === "panel_kit" &&
      item.classification.absorbsExtraPanelShipping
  );
  const requiresPanelCharge =
    standalonePanelQuantity >= 1 &&
    standalonePanelQuantity < 8 &&
    !hasQualifyingPanelKit;
  const legacySmallOnly = items.every(
    (item) => item.legacyEncodedGrams < 80
  );
  const smallQuantity = items.reduce(
    (total, item) => total + item.quantity,
    0
  );

  let totalPrice = basePrice;
  let priceReason = "destination_base";

  if (legacySmallOnly && country === "DE") {
    totalPrice = smallQuantity < 8 ? 390 : 0;
    priceReason = smallQuantity < 8 ? "small_under_8" : "small_8_or_more";
  }

  if (hasFreight) {
    totalPrice = freightPriceForCountry(country);
    priceReason = "freight";
  }

  if (country !== "DE" && hasSolarPanel) {
    totalPrice = 7900;
    priceReason = "solar_panel_international_fixed";
  } else if (requiresPanelCharge) {
    // The panel charge replaces destination base/freight pricing. It is never
    // added on top of €14.90, €19.90, €49, or €69.
    totalPrice = 7900;
    priceReason = "standalone_panel_under_8";
  }

  const sla = determineSla(items, now, country);
  const servicePrefix = requiresSpedition
    ? "Standard Spedition"
    : "Standard";
  const rate = {
    service_name: `${servicePrefix} ( ${sla.serviceLabel} )`,
    service_code: SERVICE_CODE,
    total_price: String(totalPrice),
    description: sla.description,
    currency: "EUR"
  };

  return {
    rates: [rate],
    reason: "ok",
    diagnostics: {
      country,
      priceReason,
      sla: sla.key,
      standalonePanelQuantity,
      hasQualifyingPanelKit,
      hasSolarPanel,
      hasFreight,
      requiresSpedition
    }
  };
}

function calculateRates(payload, options) {
  const decision = calculateDecision(payload, options);
  return { rates: decision.rates };
}

return Object.freeze({ calculateDecision, calculateRates });
})();

  const DEFAULT_COUNTRIES = new Set('AT,BE,BG,CZ,DE,DK,EE,ES,FI,FR,GR,HR,HU,IE,IT,LT,LU,LV,NL,PL,PT,RO,SE,SI,SK'.split(','));
  const SHIPPING_CLASSES = new Set(['standard', 'small', 'standalone_panel', 'panel_kit', 'freight']);
  const SHIPPING_MODES = new Set(['local', 'remote', 'preorder']);
  const own = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

  // Exact, handle-scoped cleanup for legacy delivery claims that conflict with live Smart Shipping data.
  // Price-threshold copy such as "Kostenloser Versand ab …" is deliberately outside this allowlist.
  const LEGACY_DELIVERY_RULES = [
    {
      handles: 'hoymiles-hms-1600-4t-wechselrichter-drosseln hoymiles-hms-2000-4t-wechselrichter-drosseln-auf-600w-800w hoymiles-hms-1600-4wb-hiflow-wechselrichter-drosseln-auf-600w-800w hoymiles-hms-2000-4wb-hiflow-wechselrichter-drosseln-auf-600w-800w zendure-solarflow-2400-pro-mit-4x-ab3000l-batteriespeicher zendure-solarflow-2400-pro-set-mit-4-500w-solarmodule-komplettset'.split(' '),
      old: 'Auf Lager und sofort lieferbar.',
      replacement: '',
    },
    {
      handles: 'zendure-solarflow-2400-ac-mit-2x-ab3000x zendure-solarflow-ab3000x-erweiterungsbatterie-2880wh-lifepo4-add-on zendure-solarflow-ab3000x-erweiterungsbatterie-2880wh-lifepo4-add-on-nach-rabatt'.split(' '),
      old: 'Auf Lager und sofort lieferbar!',
      replacement: '',
    },
    {
      handles: 'balkonkraftwerk-speicher-zendure-solarflow-set-960wh-7680wh zendure-solarflow-pvhub-mit-1x-zendure-ab2000'.split(' '),
      old: 'AB2000 Batterien, Hub1200s und Hub2000s sind schon auf Lager und sofort lieferbar.',
      replacement: '',
    },
    {
      handles: ['zendure-solarflow-set-5760wh-smart-pv-hub-1200-mit-3x-zendure-solarflow-ab-2000'],
      old: 'AB2000S Batterien, Hub1200s und Hub2000s sind schon auf Lager und sofort lieferbar.',
      replacement: '',
    },
    {
      handles: ['zendure-solarflow-ab-2000-erweiterungsbatterie-1920wh-add-on-lifepo4'],
      old: 'Auf Lager und sofort lieferbar! Wir werden Ihnen die neuesten AB2000X-Batterien zusenden, die über das neueste Design verfügen und vollständig kompatibel mit AB2000 und AB2000S sind. Zendure stellt das Produkt AB2000 nicht mehr her, und das Modell AB2000X hat das Modell AB2000S vollständig ersetzt.',
      replacement: 'Wir werden Ihnen die neuesten AB2000X-Batterien zusenden, die über das neueste Design verfügen und vollständig kompatibel mit AB2000 und AB2000S sind. Zendure stellt das Produkt AB2000 nicht mehr her, und das Modell AB2000X hat das Modell AB2000S vollständig ersetzt.',
    },
    {
      handles: 'huawei-luna2000-7-s1-energiespeichersystem huawei-luna2000-14-s1-energiespeichersystem-13-8kwh huawei-luna2000-21-s1-energiespeichersystem'.split(' '),
      old: 'Lagerware! Auf Lager und sofort lieferbar!',
      replacement: '',
    },
    {
      handles: 'zendure-solarflow-set-1920wh-hyper-2000-mit-1x-zendure-ab2000 zendure-solarflow-hyper-2000-mit-1x-zendure-ab2000 zendure-solarflow-ab2000x-erweiterungsbatterie-1920wh-lifepo4-add-on'.split(' '),
      old: 'Auf Lager und sofort lieferbar! Wir werden Ihnen die neuesten AB2000X-Batterien zusenden, die über das neueste Design verfügen und vollständig kompatibel mit AB2000 und AB2000S sind.',
      replacement: 'Wir werden Ihnen die neuesten AB2000X-Batterien zusenden, die über das neueste Design verfügen und vollständig kompatibel mit AB2000 und AB2000S sind.',
    },
    {
      handles: ['zendure-solarflow-ab2000s-erweiterungsbatterie-1920wh-add-on-lifepo4'],
      old: 'Auf Lager und sofort lieferbar! Wir werden Ihnen genau AB2000S-Batterien zusenden.',
      replacement: 'Wir werden Ihnen genau AB2000S-Batterien zusenden.',
    },
    {
      handles: ['anker-solix-solarbank-2-e1600-pro-speicher-3200wh-komplettset'],
      old: 'Anker SOLIX Solarbank 2 E1600 Pro Kmplettset - Auf Lager und sofort lieferbar.',
      replacement: 'Anker SOLIX Solarbank 2 E1600 Pro Kmplettset',
    },
    {
      handles: ['zendure-solarflow-hyper-2000-hy-brid-wech-sel-rich-ter-nach-rabatt'],
      old: 'Zendure Hyper 2000 - Nicht nur für Indach Photovoltaik, sondern auch für Balkonkraftwerk. Auf Lager und sofort lieferbar!',
      replacement: 'Zendure Hyper 2000 - Nicht nur für Indach Photovoltaik, sondern auch für Balkonkraftwerk.',
    },
    {
      handles: ['zendure-solarflow-ab2000s-erweiterungsbatterie-1920wh-add-on-lifepo4-nach-rabatt'],
      old: 'Auf Lager und sofort lieferbar! Wir werden Ihnen die neuesten AB2000X-Batterien zusenden, die über das neueste Design verfügen und vollständig kompatibel mit AB2000 und AB2000S sind. Zendure stellt das Produkt AB2000S nicht mehr her, und das Modell AB2000X hat das Modell AB2000S vollständig ersetzt.',
      replacement: 'Wir werden Ihnen die neuesten AB2000X-Batterien zusenden, die über das neueste Design verfügen und vollständig kompatibel mit AB2000 und AB2000S sind. Zendure stellt das Produkt AB2000S nicht mehr her, und das Modell AB2000X hat das Modell AB2000S vollständig ersetzt.',
    },
    {
      handles: ['zendure-solarflow-ab-2000-erweiterungsbatterie-1920wh-add-on-lifepo4-nach-rabatt'],
      old: 'Auf Lager und sofort lieferbar! Wir werden Ihnen die neuesten AB2000X-Batterien zusenden, die über das neueste Design verfügen und vollständig kompatibel mit AB2000 und AB2000S sind. Zendure stellt das Produkt AB2000 nicht mehr her, und das Modell AB2000X hat das Modell AB2000S vollständig ersetzt.',
      replacement: 'Wir werden Ihnen die neuesten AB2000X-Batterien zusenden, die über das neueste Design verfügen und vollständig kompatibel mit AB2000 und AB2000S sind. Zendure stellt das Produkt AB2000 nicht mehr her, und das Modell AB2000X hat das Modell AB2000S vollständig ersetzt.',
    },
    {
      handles: ['anker-solix-solarbank-2-e1600-pro-3200wh-komplettset-vorbestellung'],
      old: 'Anker SOLIX Solarbank 2 E1600 Pro 4300 Wh Komplettset - Auf Lager und sofort lieferbar.',
      replacement: 'Anker SOLIX Solarbank 2 E1600 Pro 4300 Wh Komplettset',
    },
    {
      handles: 'zendure-solarflow-2400-ac-batteriespeicher zendure-solarflow-2400-ac-mit-1x-ab3000x zendure-solarflow-2400-ac-mit-3x-ab3000x'.split(' '),
      old: 'Zendure SolarFlow 2400 AC - Auf Lager und sofort lieferbar!',
      replacement: 'Zendure SolarFlow 2400 AC',
    },
    {
      handles: ['anker-solix-solarbank-2-e1600-pro-anker-solix-bp2700'],
      old: 'Anker SOLIX Solarbank 2 E1600 Pro + Anker SOLIX BP2700 - Auf Lager und sofort lieferbar.',
      replacement: 'Anker SOLIX Solarbank 2 E1600 Pro + Anker SOLIX BP2700',
    },
    {
      handles: 'zendure-solarflow-2400-pro-bidirektionaler-ac-speicher zendure-solarflow-2400-pro-mit-1x-ab3000l-bidirektional-batteriespeicher zendure-solarflow-2400-pro-mit-3x-ab3000l-bidirektional-ac-batteriespeicher'.split(' '),
      old: 'Zendure SolarFlow 2400 Pro - Auf Lager und sofort lieferbar.',
      replacement: 'Zendure SolarFlow 2400 Pro',
    },
    {
      handles: ['zendure-solarflow-2400-pro-mit-2x-ab3000l-bidirektionaler-ac-speicher'],
      old: 'Zendure SolarFlow 2400 Pro mit 2x AB3000L. Auf Lager und sofort lieferbar.',
      replacement: 'Zendure SolarFlow 2400 Pro mit 2x AB3000L.',
    },
    {
      handles: ['anker-solix-c2000-gen-2-portable-power-station-2048wh-2400w'],
      old: 'Anker SOLIX C2000 Gen 2 Portable Power Station. Auf Lager und sofort lieferbar!',
      replacement: 'Anker SOLIX C2000 Gen 2 Portable Power Station.',
    },
    {
      handles: 'anker-solix-solarbank-3-e2700-pro anker-solix-solarbank-3-e2700-pro-speicher-8-04kwh-komplettset anker-solix-solarbank-3-e2700-pro-speicher-2-68kwh-komplettset anker-solix-solarbank-3-e2700-pro-speicher-2-68kwh-mit-smart-meter anker-solix-solarbank-3-e2700-pro-bp2700-smart-meter anker-solix-solarbank-3-pro-speicher-bp2700-erweiterungsakku anker-solix-solarbank-3-e2700-pro-bp2700-smart-meter-2'.split(' '),
      old: 'Alle Waren sind auf Lager und sofort lieferbar. Die Anker Solix Solarbank 3 Lieferzeit beträgt voraussichtlich 2-3 Werktage.',
      replacement: '',
    },
    {
      handles: 'anker-solix-bp2700-erweiterungsakku-fur-solarbank-2-3 anker-solix-bp2700-erweiterungsakku-fur-solarbank-2-3-nach-rabatt'.split(' '),
      old: 'Alle Waren sind auf Lager und sofort lieferbar. Die Anker Solix BP2700 Lieferzeit beträgt voraussichtlich 2-3 Werktage.',
      replacement: '',
    },
    {
      handles: 'anker-solix-solarbank-3-e2700-pro-speicher-5-36-kwh-komplettset anker-solix-solarbank-3-e2700-pro-speicher-10-72kwh-komplettset'.split(' '),
      old: 'Anker SOLIX Solarbank 3 E2700 Pro Speicher Komplettset - Alle Waren sind auf Lager und sofort lieferbar. Die Anker Solix Solarbank 3 Lieferzeit beträgt voraussichtlich 2-3 Werktage.',
      replacement: 'Anker SOLIX Solarbank 3 E2700 Pro Speicher Komplettset',
    },
    {
      handles: ['hoymiles-hms-1000-2wb-hiflow-mikrowechselrichter-wifi-integriert'],
      old: 'Auf Lager! Lieferzeit 2 - 3 Werktage.',
      replacement: '',
    },
    {
      handles: ['hoymiles-erweiterungsset-mit-verbindungskabel'],
      old: 'Hoymiles Erweiterungsset mit Verbindungskabel. Lieferzeit: 2 - 3 Werkage!',
      replacement: 'Hoymiles Erweiterungsset mit Verbindungskabel.',
    },
    {
      handles: ['hoymiles-hms-800-2wb-hiflow-mikrowechselrichter'],
      old: 'Vorbestellung: ab 20.07.2026.',
      replacement: '',
    },
    {
      handles: ['hoymiles-hms-1800-4wb-hiflow-wechselrichter-drosseln-auf-600w-800w'],
      old: 'Vorbestellung – Liefertermin wird bekannt gegeben.',
      replacement: '',
    },
    {
      handles: 'anker-solix-solarbank-max-ac-ac-gekoppelter-heimspeicher anker-solix-solarbank-max-ac-bp7000-x1-plug-play-komplettset'.split(' '),
      old: 'Anker SOLIX Solarbank MAX AC Vorbestellung: Profitieren Sie von unserer exklusiven Vorverkaufsaktion und erhalten Sie kostenlos 1× Anker SOLIX Smart Meter Gen 2 sowie 4× Anker SOLIX Smart Plugs dazu!',
      replacement: 'Anker SOLIX Solarbank MAX AC: Profitieren Sie von unserer exklusiven Vorverkaufsaktion und erhalten Sie kostenlos 1× Anker SOLIX Smart Meter Gen 2 sowie 4× Anker SOLIX Smart Plugs dazu!',
    },
    {
      handles: ['anker-solix-bp7000-erweiterungsakku-fur-max-ac'],
      old: 'Anker SOLIX BP7000 Erweiterungsakku für MAX AC. Anker SOLIX BP7000 Erweiterungsakku Vorbestellung: Profitieren Sie von unserer exklusiven Vorverkaufsaktion und erhalten Sie kostenlos 2× Anker SOLIX Smart Plugs dazu!',
      replacement: 'Anker SOLIX BP7000 Erweiterungsakku für MAX AC. Anker SOLIX BP7000 Erweiterungsakku: Profitieren Sie von unserer exklusiven Vorverkaufsaktion und erhalten Sie kostenlos 2× Anker SOLIX Smart Plugs dazu!',
    },
    {
      handles: ['anker-solix-solarbank-max-ac-bp7000-x2-21kwh-plug-play-komplettset'],
      old: 'Anker SOLIX Solarbank MAX AC Vorbestellung: Profitieren Sie von unserer exklusiven Vorverkaufsaktion und erhalten Sie kostenlos 1× Anker SOLIX Smart Meter Gen 2.',
      replacement: 'Anker SOLIX Solarbank MAX AC: Profitieren Sie von unserer exklusiven Vorverkaufsaktion und erhalten Sie kostenlos 1× Anker SOLIX Smart Meter Gen 2.',
    },
    {
      handles: 'anker-solix-solarbank-max-ac-3-bp7000-28kwh anker-solix-solarbank-max-ac-bp7000-x4-35kwh-plug-play-komplettset anker-solix-solarbank-max-ac-1-bp7000-14kwh-plug-play-set-nach-rabatt'.split(' '),
      old: 'Anker SOLIX Solarbank MAX AC Vorbestellung: Profitieren Sie von unserer exklusiven Vorverkaufsaktion und erhalten Sie kostenlos 1× Anker SOLIX Smart Meter Gen 2!',
      replacement: 'Anker SOLIX Solarbank MAX AC: Profitieren Sie von unserer exklusiven Vorverkaufsaktion und erhalten Sie kostenlos 1× Anker SOLIX Smart Meter Gen 2!',
    },
    {
      handles: ['anker-solix-solarbank-max-ac-7-42kwh-plug-play-komplettset'],
      old: 'Anker SOLIX Solarbank Max AC 7-42 kWh Plug&Play Komplettset. Anker SOLIX Solarbank MAX AC Vorbestellung: Profitieren Sie von unserer exklusiven Vorverkaufsaktion und erhalten Sie kostenlos 1×Anker SOLIX Smart Meter Gen 2!',
      replacement: 'Anker SOLIX Solarbank Max AC 7-42 kWh Plug&Play Komplettset. Anker SOLIX Solarbank MAX AC: Profitieren Sie von unserer exklusiven Vorverkaufsaktion und erhalten Sie kostenlos 1×Anker SOLIX Smart Meter Gen 2!',
    },
  ];
  const legacyDeliveryRuleByHandle = new Map();
  LEGACY_DELIVERY_RULES.forEach((rule) => rule.handles.forEach((handle) => legacyDeliveryRuleByHandle.set(handle, rule)));
  const LEGACY_SPACE = new Set([' ', '\t', '\n', '\r', '\f', '\u00a0']);

  const legacyDeletionRange = (oldText, newText) => {
    let start = 0;
    while (start < oldText.length && start < newText.length && oldText[start] === newText[start]) start += 1;
    let suffix = 0;
    while (
      suffix < oldText.length - start
      && suffix < newText.length - start
      && oldText[oldText.length - 1 - suffix] === newText[newText.length - 1 - suffix]
    ) suffix += 1;
    const end = oldText.length - suffix;
    return start < end && oldText.slice(0, start) + oldText.slice(end) === newText
      ? { start, end }
      : null;
  };

  if (legacyDeliveryRuleByHandle.size !== 57) throw new Error('[Mini Power Shipping] Expected 57 exact description cleanup rules.');
  legacyDeliveryRuleByHandle.forEach((rule, handle) => {
    if (
      !handle
      || !rule
      || typeof rule.old !== 'string'
      || typeof rule.replacement !== 'string'
      || rule.old.includes('Kostenloser Versand ab')
      || rule.replacement.includes('Kostenloser Versand ab')
      || !legacyDeletionRange(rule.old, rule.replacement)
    ) {
      throw new Error(`[Mini Power Shipping] Invalid exact description cleanup rule: ${handle}`);
    }
  });

  const readLegacyBlockText = (element) => {
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];
    let raw = '';
    let node;
    while ((node = walker.nextNode())) {
      const start = raw.length;
      raw += node.data;
      nodes.push({ node, start, end: raw.length });
    }

    const chars = [];
    const ranges = [];
    let index = 0;
    while (index < raw.length) {
      if (LEGACY_SPACE.has(raw[index])) {
        const start = index;
        while (index < raw.length && LEGACY_SPACE.has(raw[index])) index += 1;
        if (chars.length && index < raw.length) {
          chars.push(' ');
          ranges.push([start, index]);
        }
      } else {
        chars.push(raw[index]);
        ranges.push([index, index + 1]);
        index += 1;
      }
    }
    return { text: chars.join(''), ranges, nodes };
  };

  const deleteLegacyRawRange = (nodes, start, end) => {
    nodes.forEach((span) => {
      const from = Math.max(start, span.start);
      const to = Math.min(end, span.end);
      if (from < to) span.node.deleteData(from - span.start, to - from);
    });
  };

  const auditLegacyCleanup = (detail) => {
    const record = Object.freeze({
      version: 'mp-description-cleanup-v1',
      targetThemeId: '199771291995',
      at: new Date().toISOString(),
      ...detail,
    });
    window.__MP_DESCRIPTION_CLEANUP_AUDIT__ = record;
    window.dispatchEvent(new CustomEvent('mp:description-cleanup', { detail: record }));
    return record;
  };

  const cleanLegacyDeliveryClaim = (root) => {
    const actualThemeId = String(window.Shopify?.theme?.id || '199771291995');
    if (actualThemeId !== '199771291995') {
      auditLegacyCleanup({ status: 'skipped-theme', actualThemeId });
      return;
    }

    const handle = String(root.dataset.mpProductHandle || '');
    const rule = legacyDeliveryRuleByHandle.get(handle);
    if (!rule) return;

    const matches = [...root.querySelectorAll('p, div, li')]
      .filter((block) => !block.querySelector('p, div, li'))
      .map((block) => ({ block, reading: readLegacyBlockText(block) }))
      .filter(({ reading }) => reading.text === rule.old);
    if (matches.length !== 1) {
      root.dataset.mpDescriptionCleanup = 'skipped';
      auditLegacyCleanup({ status: 'skipped-exact-match-count', handle, count: matches.length });
      console.error('[Mini Power Shipping] Exact legacy delivery block count was not one.', handle, matches.length);
      return;
    }

    const { block, reading } = matches[0];
    if (!rule.replacement) {
      block.remove();
    } else {
      const cut = legacyDeletionRange(rule.old, rule.replacement);
      const snapshot = block.innerHTML;
      const rawStart = reading.ranges[cut.start][0];
      const rawEnd = reading.ranges[cut.end - 1][1];
      deleteLegacyRawRange(reading.nodes, rawStart, rawEnd);
      if (readLegacyBlockText(block).text !== rule.replacement) {
        block.innerHTML = snapshot;
        root.dataset.mpDescriptionCleanup = 'rolled-back';
        auditLegacyCleanup({ status: 'rolled-back-verification', handle });
        console.error('[Mini Power Shipping] Legacy delivery cleanup verification failed and was rolled back.', handle);
        return;
      }
    }

    root.dataset.mpDescriptionCleanup = 'applied';
    auditLegacyCleanup({ status: 'applied', handle });
  };

  const todayUtc = (now = new Date()) => now.toISOString().slice(0, 10);

  const isDateKey = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
  };

  const classificationFor = (variant, classifications) => {
    if (!variant || !classifications || typeof classifications !== 'object') return null;
    const direct = classifications[String(variant.id)];
    const taxPeer = String(variant.sku || '').match(/^MP-TAX-(\d+)$/);
    const source = direct || (taxPeer ? classifications[taxPeer[1]] : null);
    if (!source || typeof source !== 'object') return null;
    const result = { ...source };
    const shipping = variant.shipping || {};
    if (SHIPPING_MODES.has(shipping.mode)) {
      result.l = String(shipping.mode || '').trim().toLowerCase();
      result.d = String(shipping.available_from || '').trim();
    }
    if (['standalone_panel', 'panel_kit'].includes(shipping.panel_role)) {
      result.c = shipping.panel_role;
      result.s = true;
    } else if (shipping.panel_role === 'none') {
      if (['standalone_panel', 'panel_kit'].includes(result.c)) {
        result.s = false;
        result.c = Number(result.w) > 40000 ? 'freight' : 'standard';
      }
    }
    if (Number.isInteger(shipping.panel_quantity) && shipping.panel_quantity >= 0) result.p = shipping.panel_quantity;
    if (typeof shipping.absorbs_extra_panel_shipping === 'boolean') result.x = shipping.absorbs_extra_panel_shipping;
    return result;
  };

  const unavailable = (reason) => ({
    status: 'unavailable',
    reason,
    mode: '',
    sourceMode: '',
    minDays: null,
    maxDays: null,
    availableFrom: '',
    requiresSpedition: false,
  });

  const calculate = ({ variant = {}, classification, country = 'DE', quantity = 1, now = new Date() } = {}) => {
    if (!classification) return unavailable('unknown_variant');
    const shipping = variant.shipping || {};
    const mode = SHIPPING_MODES.has(shipping.mode) ? shipping.mode : classification.l;
    const date = SHIPPING_MODES.has(shipping.mode) ? shipping.available_from : classification.d;
    if (!SHIPPING_MODES.has(mode)) return unavailable('missing_delivery_mode');
    const id = String(variant.id || 'selected');
    const record = {
      status: 'ACTIVE',
      shipping_class: classification.c,
      lead_class: mode,
      available_from: date,
      de_delivery_days_min: Number(shipping.de_delivery_days_min ?? classification.n),
      de_delivery_days_max: Number(shipping.de_delivery_days_max ?? classification.z),
      actual_unit_weight_grams: Number(classification.w),
      weight_source: 'billbee',
      panel_qty_in_bundle: Number(classification.p),
      absorbs_extra_panel_shipping: classification.x === true,
      contains_solar_panel: classification.s === true,
      allowed_country_codes: classification.a == null ? [...DEFAULT_COUNTRIES] : String(classification.a).split(','),
    };
    const decision = canonicalShipping.calculateDecision({
      rate: {
        currency: 'EUR',
        destination: { country: String(country || 'DE').toUpperCase() },
        items: [{ variant_id: id, quantity: Math.max(1, Number.parseInt(quantity, 10) || 1), grams: Number(variant.weight_grams) || 0 }],
      },
    }, { classificationMap: { [id]: record }, now });
    if (decision.reason !== 'ok' || decision.rates.length !== 1) return unavailable(decision.reason);
    const rate = decision.rates[0];
    const range = rate.service_name.match(/(\d+)-(\d+) Werktage/);
    const preorder = decision.diagnostics.sla.startsWith('preorder:');
    return {
      status: 'ok',
      reason: mode === 'preorder' && !preorder ? 'stale_or_missing_preorder_date' : '',
      mode: preorder ? 'preorder' : mode === 'preorder' ? 'fallback' : mode,
      sourceMode: mode,
      minDays: range ? Number(range[1]) : null,
      maxDays: range ? Number(range[2]) : null,
      availableFrom: preorder ? decision.diagnostics.sla.slice(9) : '',
      requiresSpedition: decision.diagnostics.requiresSpedition,
      amountCents: Number(rate.total_price),
    };
  };
  const resolve = (input = {}) => calculate(input);
  const quote = ({ variant = {}, classifications = {}, ...input } = {}) => calculate({
    ...input,
    variant,
    classification: classificationFor(variant, classifications),
  });

  const resolveCart = ({ items = [], country = 'DE', now = new Date() } = {}) => {
    if (!Array.isArray(items) || items.length === 0) return unavailable('empty_cart');
    const map = {};
    const lines = [];
    let available = true;
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      const variant = item?.variant || {};
      const c = classificationFor(variant, item?.classifications || {});
      if (!c) return unavailable('unknown_variant');
      const shipping = variant.shipping || {};
      const id = String(i);
      map[id] = { status: 'ACTIVE', shipping_class: c.c, lead_class: c.l,
        available_from: c.d, actual_unit_weight_grams: Number(c.w), weight_source: 'billbee',
        panel_qty_in_bundle: Number(c.p), absorbs_extra_panel_shipping: c.x === true,
        contains_solar_panel: c.s === true, allowed_country_codes: String(c.a || '').split(','),
        de_delivery_days_min: Number(shipping.de_delivery_days_min ?? c.n),
        de_delivery_days_max: Number(shipping.de_delivery_days_max ?? c.z) };
      lines.push({ variant_id: id, quantity: Math.max(1, Number(item.quantity) || 1), grams: Number(variant.weight_grams) || 0 });
      available = available && Boolean(variant.available);
    }
    const decision = canonicalShipping.calculateDecision({rate:{currency:'EUR',destination:{country},items:lines}}, {classificationMap:map,now});
    if (decision.reason !== 'ok' || decision.rates.length !== 1) return unavailable(decision.reason);
    const rate = decision.rates[0];
    const range = rate.service_name.match(/(\d+)-(\d+) Werktage/);
    const preorder = decision.diagnostics.sla.startsWith('preorder:');
    return { status:'ok', reason:'', mode:preorder?'preorder':'local', sourceMode:preorder?'preorder':'local',
      minDays:range?Number(range[1]):null,maxDays:range?Number(range[2]):null,
      availableFrom:preorder?decision.diagnostics.sla.slice(9):'',
      requiresSpedition:decision.diagnostics.requiresSpedition,amountCents:Number(rate.total_price),available };
  };

  const formatDate = (value, locale = document.documentElement.lang || 'de-DE') => {
    if (!isDateKey(String(value || ''))) return String(value || '');
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(`${value}T12:00:00Z`));
  };

  const text = (result, { available = true, locale = document.documentElement.lang || 'de-DE' } = {}) => {
    const german = String(locale).toLowerCase().startsWith('de');
    if (!result || result.status !== 'ok') {
      return german ? 'Lieferzeit wird im Checkout berechnet' : 'Delivery time is calculated at checkout';
    }
    if (result.mode === 'preorder') {
      const date = formatDate(result.availableFrom, locale);
      return german ? `Versand ab ${date}` : `Ships from ${date}`;
    }
    if (!available) return german ? 'Lieferzeit: derzeit nicht verfügbar' : 'Delivery time: currently unavailable';
    return german
      ? `Voraussichtliche Lieferzeit: ${result.minDays}–${result.maxDays} Werktage`
      : `Estimated delivery time: ${result.minDays}–${result.maxDays} business days`;
  };

  // Format Smart Shipping labels, including explicit local delivery-day metafields.
  const serviceName = (result, { available = true, locale = document.documentElement.lang || 'de-DE' } = {}) => {
    const german = String(locale).toLowerCase().startsWith('de');
    if (!result || result.status !== 'ok') {
      return german ? 'Lieferzeit wird im Checkout berechnet' : 'Delivery time is calculated at checkout';
    }
    if (!available && result.mode !== 'preorder') {
      return german ? 'Lieferzeit: derzeit nicht verfügbar' : 'Delivery time: currently unavailable';
    }

    if (result.mode === 'preorder') {
      return `${result.requiresSpedition ? 'Standard Spedition' : 'Standard'} ( Versand ab ${formatDate(result.availableFrom, 'de-DE')} )`;
    }
    const prefix = result.requiresSpedition ? 'Standard Spedition' : 'Standard';
    return `${prefix} ( ${result.minDays}-${result.maxDays} Werktage )`;
  };

  const availabilityText = (result, { available = true, locale = document.documentElement.lang || 'de-DE' } = {}) => {
    const german = String(locale).toLowerCase().startsWith('de');
    if (result?.status === 'ok' && result.mode === 'preorder') {
      const date = formatDate(result.availableFrom, locale);
      return german ? `Verfügbar ab ${date}` : `Available from ${date}`;
    }
    if (!available) return german ? 'Derzeit nicht verfügbar' : 'Currently unavailable';
    return german ? 'Verfügbar' : 'Available';
  };

  const hydrateHomeCard = (card) => {
    const node = card.querySelector('[data-mp-home-delivery-data]');
    if (!node) return;
    try {
      const config = JSON.parse(node.textContent || '{}');
      const variant = config.variant || {};
      const classification = classificationFor(variant, config.classifications || {});
      const result = resolve({
        variant,
        classification,
        country: card.dataset.deliveryCountry || 'DE',
      });
      const available = Boolean(variant.available);
      const deliveryNode = card.querySelector('[data-mp-home-delivery-text]');
      const availabilityNode = card.querySelector('[data-mp-home-availability-text]');
      const shippingRow = card.querySelector('.mp-home-product-card__shipping');
      const availabilityRow = availabilityNode?.closest('.mp-home-product-card__meta-item');
      if (deliveryNode) deliveryNode.textContent = serviceName(result, { available });
      if (availabilityNode) availabilityNode.textContent = availabilityText(result, { available });
      if (shippingRow) shippingRow.classList.toggle('mp-home-product-card__shipping--unavailable', result.status !== 'ok' || (!available && result.mode !== 'preorder'));
      if (availabilityRow) availabilityRow.classList.toggle('mp-home-product-card__meta-item--unavailable', result.status !== 'ok' || (!available && result.mode !== 'preorder'));
    } catch (error) {
      console.error('[Mini Power Shipping] Homepage delivery data could not be read.', error);
    }
  };

  const cartDelivery = (container) => {
    const node = container.querySelector('[data-mp-cart-delivery-data]');
    if (!node) return null;
    const config = JSON.parse(node.textContent || '{}');
    const result = resolveCart({
      items: config.items || [],
      country: config.country || 'DE',
    });
    return {
      result,
      value: text(result, { available: result.available !== false }),
    };
  };

  const compactCartDeliveryText = (value) => value
    .replace(/^Voraussichtliche Lieferzeit:\s*/i, '')
    .replace(/^Lieferzeit wird\s*/i, 'wird ')
    .replace(/^Lieferzeit:\s*/i, '')
    .replace(/^Versand ab\s*/i, 'ab ');

  const hydrateCartSummary = (summary) => {
    const deliveryNode = summary.querySelector('[data-mp-cart-delivery-text]');
    if (!deliveryNode) return;
    try {
      const delivery = cartDelivery(summary);
      if (!delivery) return;
      deliveryNode.textContent = delivery.value;
      summary.classList.toggle('is-unavailable', delivery.result.status !== 'ok' || delivery.result.available === false);
      summary.dataset.deliveryMode = delivery.result.mode || 'unavailable';
    } catch (error) {
      summary.classList.add('is-unavailable');
      console.error('[Mini Power Shipping] Cart delivery data could not be read.', error);
    }
  };

  const hydrateCartInline = (container) => {
    const deliveryNode = container.querySelector('[data-mp-cart-delivery-inline-text]');
    if (!deliveryNode) return;
    try {
      const delivery = cartDelivery(container);
      if (!delivery) return;
      deliveryNode.textContent = compactCartDeliveryText(delivery.value);
      container.dataset.deliveryMode = delivery.result.mode || 'unavailable';
    } catch (error) {
      console.error('[Mini Power Shipping] Inline cart delivery data could not be read.', error);
    }
  };

  const init = (scope = document) => {
    if (scope.matches?.('[data-mp-home-delivery-card]')) hydrateHomeCard(scope);
    scope.querySelectorAll?.('[data-mp-home-delivery-card]').forEach(hydrateHomeCard);
    if (scope.matches?.('[data-mp-description-fallback]')) cleanLegacyDeliveryClaim(scope);
    scope.querySelectorAll?.('[data-mp-description-fallback]').forEach(cleanLegacyDeliveryClaim);
    if (scope.matches?.('[data-mp-cart-delivery]')) hydrateCartSummary(scope);
    scope.querySelectorAll?.('[data-mp-cart-delivery]').forEach(hydrateCartSummary);
    if (scope.matches?.('[data-mp-cart-delivery-inline]')) hydrateCartInline(scope);
    scope.querySelectorAll?.('[data-mp-cart-delivery-inline]').forEach(hydrateCartInline);
  };

  window.MPSmartShippingDelivery = Object.freeze({
    availabilityText,
    classificationFor,
    formatDate,
    resolve,
    quote,
    resolveCart,
    serviceName,
    text,
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  const cartDeliveryObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (
          node.matches('[data-mp-cart-delivery], [data-mp-cart-delivery-inline]') ||
          node.querySelector('[data-mp-cart-delivery], [data-mp-cart-delivery-inline]')
        ) init(node);
      });
    });
  });
  cartDeliveryObserver.observe(document.documentElement, { childList: true, subtree: true });
})();

